'use client';

import { useCallback, useEffect, useReducer } from 'react';
import { HERO_TEAMS_SETTLE_FALLBACK_MS } from './Chero.constants';
import type { HeroTeamsVisibilityState } from './Chero.types';

interface UseHeroTeamsVisibilityInput {
  hasTeams: boolean;
  viewportLoaded: boolean;
}

interface UseHeroTeamsVisibilityResult extends HeroTeamsVisibilityState {
  markTeamsSettled: () => void;
}

enum HeroTeamsVisibilityActionType {
  Reset = 'reset',
  Mount = 'mount',
  Settle = 'settle',
  Show = 'show',
}

interface HeroTeamsVisibilityAction {
  type: HeroTeamsVisibilityActionType;
}

type HeroTeamsVisibilityStateResolver = (
  state: HeroTeamsVisibilityState,
) => HeroTeamsVisibilityState;

const INITIAL_HERO_TEAMS_VISIBILITY_STATE: HeroTeamsVisibilityState = {
  isTeamsMounted: false,
  isTeamsSettled: false,
  isTeamsVisible: false,
};

const HERO_TEAMS_VISIBILITY_STATE_BY_ACTION: Record<
  HeroTeamsVisibilityActionType,
  HeroTeamsVisibilityStateResolver
> = {
  [HeroTeamsVisibilityActionType.Reset]: (state) => {
    if (
      !state.isTeamsMounted &&
      !state.isTeamsSettled &&
      !state.isTeamsVisible
    ) {
      return state;
    }

    return INITIAL_HERO_TEAMS_VISIBILITY_STATE;
  },
  [HeroTeamsVisibilityActionType.Mount]: (state) => {
    if (
      state.isTeamsMounted &&
      !state.isTeamsSettled &&
      !state.isTeamsVisible
    ) {
      return state;
    }

    return {
      isTeamsMounted: true,
      isTeamsSettled: false,
      isTeamsVisible: false,
    };
  },
  [HeroTeamsVisibilityActionType.Settle]: (state) => {
    if (state.isTeamsSettled) {
      return state;
    }

    return {
      ...state,
      isTeamsSettled: true,
    };
  },
  [HeroTeamsVisibilityActionType.Show]: (state) => {
    if (state.isTeamsVisible) {
      return state;
    }

    return {
      ...state,
      isTeamsVisible: true,
    };
  },
};

function reduceHeroTeamsVisibilityState(
  state: HeroTeamsVisibilityState,
  action: HeroTeamsVisibilityAction,
): HeroTeamsVisibilityState {
  return HERO_TEAMS_VISIBILITY_STATE_BY_ACTION[action.type](state);
}

export function useHeroTeamsVisibility({
  hasTeams,
  viewportLoaded,
}: UseHeroTeamsVisibilityInput): UseHeroTeamsVisibilityResult {
  const [visibilityState, dispatchVisibilityAction] = useReducer(
    reduceHeroTeamsVisibilityState,
    INITIAL_HERO_TEAMS_VISIBILITY_STATE,
  );

  useEffect(() => {
    if (!viewportLoaded || !hasTeams) {
      dispatchVisibilityAction({ type: HeroTeamsVisibilityActionType.Reset });
      return;
    }

    dispatchVisibilityAction({ type: HeroTeamsVisibilityActionType.Mount });

    const settleTimeout = window.setTimeout(() => {
      dispatchVisibilityAction({
        type: HeroTeamsVisibilityActionType.Settle,
      });
    }, HERO_TEAMS_SETTLE_FALLBACK_MS);

    return () => {
      window.clearTimeout(settleTimeout);
    };
  }, [hasTeams, viewportLoaded]);

  useEffect(() => {
    const { isTeamsMounted, isTeamsSettled, isTeamsVisible } = visibilityState;

    if (!isTeamsMounted || !isTeamsSettled || isTeamsVisible) {
      return;
    }

    dispatchVisibilityAction({ type: HeroTeamsVisibilityActionType.Show });
  }, [visibilityState]);

  const markTeamsSettled = useCallback(() => {
    dispatchVisibilityAction({ type: HeroTeamsVisibilityActionType.Settle });
  }, []);

  return {
    ...visibilityState,
    markTeamsSettled,
  };
}
